import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { LogService } from '../../../../services/log-service';
import { getPackingslipsSettingsPath } from '../../../../routes/constants';
import { useHandlerTracking } from '../../hooks/use-handler-tracking';
import { PACKING_SLIP_UPDATE_ACTION_ID } from '../../llm-action-registry/action-definitions';
import { EXECUTOR_SLOT, PAGE_SLOT } from '../../llm-action-registry/types';
import { useLLMActionRegistryContext } from '../../llm-action-registry/llm-action-registry-context';
import { messages } from '../../messages';
import { CAPABILITY_IDS } from '../capabilities-registry';
import style from '../../style.module.less';

const PACKING_SLIP_ROUTE = getPackingslipsSettingsPath();

const DANGEROUS_CONTENT_PATTERN = /<(?:script|iframe|object|embed)[\s>]|(?:javascript|data):|on\w+\s*=/i;

const containsDangerousContent = (content: string): boolean => {
  if (!content || typeof content !== 'string') {
    return false;
  }
  return DANGEROUS_CONTENT_PATTERN.test(content);
};

type PackingSlipParameters = {
  templateName?: string;
  name?: string;
  size?: string;
  header?: string;
  itemsHeader?: string;
  items?: string;
  footer?: string;
};

type PackingSlipHandlerProps = {
  parameters: PackingSlipParameters;
  conversationId: string;
  turnNumber: number;
};

export const PackingSlipHandler = ({ parameters, conversationId, turnNumber }: PackingSlipHandlerProps) => {
  const registry = useLLMActionRegistryContext();
  const navigate = useNavigate();
  const [isExecuting, setIsExecuting] = useState(false);

  const { templateName, name, size, header, itemsHeader, items, footer } = parameters;
  const resolvedName = name ?? templateName;

  const isValid = !!resolvedName && !!header;

  const contentSections = useMemo(
    () => [header, itemsHeader, items, footer].filter(Boolean) as string[],
    [header, itemsHeader, items, footer],
  );
  const hasDangerousContent = useMemo(() => contentSections.some(containsDangerousContent), [contentSections]);

  const trackingParameters = useMemo(() => ({ templateName: resolvedName, size }), [resolvedName, size]);

  const { trackAction } = useHandlerTracking({
    capabilityId: CAPABILITY_IDS.PACKING_SLIP,
    conversationId,
    turnNumber,
    parameters: trackingParameters as Record<string, unknown>,
    isValid: isValid && !hasDangerousContent,
  });

  const handleClick = useCallback(async () => {
    if (!header || hasDangerousContent) return;

    setIsExecuting(true);
    trackAction();

    const templateData = {
      name: resolvedName,
      size: size ?? 'Letter',
      header,
      itemsHeader: itemsHeader ?? '',
      items: items ?? '',
      footer: footer ?? '',
    };

    if (!registry.isActionBound(PACKING_SLIP_UPDATE_ACTION_ID, PAGE_SLOT)) {
      navigate(PACKING_SLIP_ROUTE);
      const pageBound = await registry.waitForBinding(PACKING_SLIP_UPDATE_ACTION_ID, PAGE_SLOT);
      if (!pageBound) {
        LogService.logError(new Error('Timed out waiting for packing slip page binding'));
        setIsExecuting(false);
        return;
      }
    }

    if (!registry.isActionBound(PACKING_SLIP_UPDATE_ACTION_ID, EXECUTOR_SLOT)) {
      await registry.triggerSlot(PACKING_SLIP_UPDATE_ACTION_ID, PAGE_SLOT);
      const editorBound = await registry.waitForBinding(PACKING_SLIP_UPDATE_ACTION_ID, EXECUTOR_SLOT);
      if (!editorBound) {
        LogService.logError(new Error('Timed out waiting for packing slip editor binding'));
        setIsExecuting(false);
        return;
      }
    }

    const result = await registry.executeAction(PACKING_SLIP_UPDATE_ACTION_ID, templateData);
    if (!result.success) {
      LogService.logError(new Error(`Packing slip executeAction failed: ${result.error}`));
    }
    setIsExecuting(false);
  }, [header, hasDangerousContent, resolvedName, size, itemsHeader, items, footer, registry, trackAction, navigate]);

  if (!isValid || hasDangerousContent) {
    return <Text textType={TextType.Caption} value={messages.capabilityError} />;
  }

  return (
    <div className={style.commandLink}>
      <Button isLoading={isExecuting} onClick={handleClick} theme={THEME_CONSTANTS.tertiary}>
        <Text textType={TextType.Paragraph} value={messages.generateTemplateButton} />
      </Button>
    </div>
  );
};
